import { forwardRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { IconNames } from '@shipengine/giger-theme';
import { ButtonVariant, ButtonSize } from '../../../utils/buttonStyles.js';
import { Typography } from '../../../components/Typography/Typography.js';
import { getDropzoneStyles, getDropzoneWrapperStyles, getTextStyles, getButtonStyles, getFileDetailsStyles, getFileNameStyles } from './DragDropUploaderStyles.js';
import { jsxs, jsx, Fragment } from '@emotion/react/jsx-runtime';
import { DefaultLabelValues } from './types/labels.js';
import { Icon } from '../../../components/Icon/Icon.js';
import { Button } from '../../../components/Button/Button.js';
import { ButtonIcon } from '../../../components/ButtonIcon/ButtonIcon.js';
import { FieldMessage } from '../../../components/Form/Field/FieldMessage/FieldMessage.js';
import { FieldMessageType } from '../../../components/Form/Field/FieldMessage/types/messageType.js';

function bytesToSize(bytes) {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes == 0) return '0 Byte';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
}
const DragDropUploader = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    className,
    'data-id': dataId,
    disabled = false,
    file,
    labels,
    name,
    onChange,
    maxSize = 200000,
    isInvalid,
    errorMessage,
    ...dropzoneProps
  } = _ref;
  const {
    isDragActive,
    getRootProps,
    getInputProps
  } = useDropzone({
    maxSize,
    multiple: false,
    onDrop: onChange,
    disabled,
    ...dropzoneProps
  });
  const handleRemove = () => {
    onChange([], [], undefined);
  };
  return jsxs("div", {
    "data-id": dataId,
    css: className,
    children: [jsxs("div", {
      css: getDropzoneStyles(isDragActive, disabled),
      ...getRootProps(),
      children: [jsx("input", {
        "data-id": "file-input",
        disabled: disabled,
        ref: ref,
        name: name,
        ...getInputProps()
      }), !isDragActive ? jsxs("div", {
        css: getDropzoneWrapperStyles,
        children: [jsx(Typography, {
          variant: "subtitle1",
          css: getTextStyles,
          children: (labels === null || labels === void 0 ? void 0 : labels.toDragAndDrop) || DefaultLabelValues.DRAG_DROP
        }), jsxs(Button, {
          css: getButtonStyles,
          variant: ButtonVariant.TEXT,
          disabled: disabled,
          size: ButtonSize.MEDIUM,
          children: [jsx(Icon, {
            name: IconNames.SEARCH
          }), jsx("span", {
            children: (labels === null || labels === void 0 ? void 0 : labels.toBrowse) || DefaultLabelValues.BROWSE
          })]
        }), jsx(Typography, {
          variant: "small",
          css: getTextStyles,
          children: (labels === null || labels === void 0 ? void 0 : labels.toFileDetails) || DefaultLabelValues.FILE_DETAILS
        })]
      }) : jsxs("div", {
        css: getDropzoneWrapperStyles,
        children: [jsx(Icon, {
          name: IconNames.DRAG_DROP
        }), jsx(Typography, {
          variant: "small",
          bold: true,
          children: (labels === null || labels === void 0 ? void 0 : labels.toDrop) || DefaultLabelValues.DROP
        })]
      })]
    }), file && jsxs(Fragment, {
      children: [jsxs("div", {
        css: getFileDetailsStyles(isInvalid),
        "data-id": "file-preview",
        children: [jsxs("div", {
          children: [jsx(Typography, {
            css: getFileNameStyles,
            component: "p",
            variant: "body2",
            bold: true,
            children: file.name
          }), jsx(Typography, {
            css: getTextStyles,
            variant: "small",
            children: `${bytesToSize(file.size)}`
          })]
        }), jsx(ButtonIcon, {
          variant: ButtonVariant.TEXT,
          onClick: handleRemove,
          disabled: disabled,
          icon: IconNames.TRASH,
          ariaDescribedby: (labels === null || labels === void 0 ? void 0 : labels.toRemove) || DefaultLabelValues.REMOVE,
          children: (labels === null || labels === void 0 ? void 0 : labels.toRemove) || DefaultLabelValues.REMOVE
        })]
      }), errorMessage && isInvalid && jsx(FieldMessage, {
        type: FieldMessageType.ERROR,
        children: errorMessage
      })]
    })]
  });
});
DragDropUploader.displayName = 'DragDropUploader';

export { DragDropUploader };
//# sourceMappingURL=DragDropUploader.js.map
